"use client";

import {createContext, useContext, useMemo, useState, useSyncExternalStore, type ReactNode} from "react";

type PendingDecision = "apply" | "discard" | "fresh";
type DecisionState = {pending: PendingDecision | null; error: boolean; requestedFresh: boolean};

function createDecisionStore() {
   let state: DecisionState = {pending: null, error: false, requestedFresh: false};
   const listeners = new Set<() => void>();
   const update = (patch: Partial<DecisionState>) => {
      state = {...state, ...patch};
      listeners.forEach(listener => listener());
   };
   return {
      getSnapshot: () => state,
      subscribe: (listener: () => void) => {
         listeners.add(listener);
         return () => {listeners.delete(listener);};
      },
      // Acquire synchronously so two mounted surfaces cannot submit the same decision.
      begin: (pending: PendingDecision) => {
         if (state.pending || (pending === "fresh" && state.requestedFresh)) return false;
         update({pending, error: false});
         return true;
      },
      update,
   };
}

type DecisionStore = ReturnType<typeof createDecisionStore>;
const ToolApprovalDecisionContext = createContext<Map<string, DecisionStore> | null>(null);

/** Client-only recovery state survives navigation between chat, dock and script review. */
export function ToolApprovalDecisionProvider({children}: {children: ReactNode}) {
   const [stores] = useState(() => new Map<string, DecisionStore>());
   return <ToolApprovalDecisionContext.Provider value={stores}>{children}</ToolApprovalDecisionContext.Provider>;
}

/** Approval IDs identify server decisions; live approval status still comes from the chat. */
export function useToolApprovalDecisionState(approvalId: string | undefined) {
   const stores = useContext(ToolApprovalDecisionContext);
   const store = useMemo(() => {
      if (!stores || !approvalId) return createDecisionStore();
      let found = stores.get(approvalId);
      if (!found) {
         found = createDecisionStore();
         stores.set(approvalId, found);
      }
      return found;
   }, [stores, approvalId]);
   const state = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
   return {state, begin: store.begin, update: store.update};
}
